/**
 * Warteliste des aktiven Mandanten.
 * Schüler, die noch keinen Unterrichtstermin bekommen, stehen hier — nicht als Belegung.
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { formatApiError } from '@/lib/errors';
import { formatTime } from '@/lib/utils';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

function personName(row) {
  if (!row) return '';
  return `${row.last_name || ''}, ${row.first_name || ''}`.replace(/^,\s*|,\s*$/g, '').trim();
}

export default function WaitlistPanel() {
  const [items, setItems] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState([]);
  const [student, setStudent] = useState(null);
  const [subjectId, setSubjectId] = useState('');
  const [day, setDay] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const res = await api.get('/waitlist');
    setItems(res.data?.items || []);
  }, []);

  useEffect(() => {
    load().catch(() => setItems([]));
    api.get('/subjects').then((res) => {
      setSubjects(Array.isArray(res.data) ? res.data : []);
    }).catch(() => setSubjects([]));
  }, [load]);

  const searchStudents = async (value) => {
    setQuery(value);
    if (value.trim().length < 2) {
      setHits([]);
      return;
    }
    try {
      const res = await api.get('/students', { params: { search: value.trim(), limit: 20 } });
      const rows = Array.isArray(res.data) ? res.data : (res.data?.items || []);
      setHits(rows);
    } catch {
      setHits([]);
    }
  };

  const resetForm = () => {
    setAdding(false);
    setQuery('');
    setHits([]);
    setStudent(null);
    setSubjectId('');
    setDay('');
    setNote('');
  };

  const save = async () => {
    if (!student?.id) {
      toast.error('Bitte einen Schüler auswählen');
      return;
    }
    if (!subjectId) {
      toast.error('Bitte ein Fach auswählen');
      return;
    }
    setSaving(true);
    try {
      await api.post('/waitlist', {
        student_id: student.id,
        subject_id: subjectId,
        preferred_day: day === '' ? null : Number(day),
        note: note.trim() || null,
      });
      toast.success('Auf die Warteliste aufgenommen');
      resetForm();
      await load();
    } catch (err) {
      toast.error(formatApiError(err, 'Warteliste konnte nicht gespeichert werden'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/waitlist/${id}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err, 'Eintrag konnte nicht entfernt werden'));
    }
  };

  return (
    <div data-testid="waitlist-panel" className="p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-xs text-slate-500">
          Schüler ohne Unterrichtstermin. Die Liste gilt nur für diesen Mandanten.
        </p>
        <button
          type="button"
          data-testid="waitlist-add"
          onClick={() => setAdding(true)}
          className="text-xs px-2 py-1 rounded bg-brand-red text-white"
        >
          Aufnehmen
        </button>
      </div>
      {adding && (
        <div className="mb-3 border border-slate-200 rounded p-2 bg-slate-50 space-y-2" data-testid="waitlist-form">
          <div className="relative">
            <input
              data-testid="waitlist-student-search"
              value={student ? personName(student) : query}
              onChange={(e) => {
                setStudent(null);
                searchStudents(e.target.value);
              }}
              placeholder="Schüler suchen…"
              className="w-full h-8 px-2 text-xs border border-slate-200 rounded bg-white"
            />
            {hits.length > 0 && !student && (
              <ul className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded shadow text-xs max-h-40 overflow-auto">
                {hits.map((row) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      data-testid={`waitlist-student-${row.id}`}
                      className="w-full text-left px-2 py-1 hover:bg-brand-red/10"
                      onClick={() => {
                        setStudent(row);
                        setHits([]);
                        setQuery(personName(row));
                      }}
                    >
                      {personName(row)}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              data-testid="waitlist-subject"
              value={subjectId}
              onChange={(e) => setSubjectId(e.target.value)}
              className="h-8 px-2 text-xs border border-slate-200 rounded bg-white"
            >
              <option value="">Fach…</option>
              {subjects.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <select
              data-testid="waitlist-day"
              value={day}
              onChange={(e) => setDay(e.target.value)}
              className="h-8 px-2 text-xs border border-slate-200 rounded bg-white"
            >
              <option value="">Wunschtag</option>
              {WEEKDAYS.map((label, index) => (
                <option key={label} value={String(index)}>{label}</option>
              ))}
            </select>
            <input
              data-testid="waitlist-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Bemerkung"
              className="h-8 px-2 text-xs border border-slate-200 rounded bg-white flex-1 min-w-[8rem]"
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="waitlist-save"
              disabled={saving}
              onClick={save}
              className="text-xs px-2 py-1 rounded bg-brand-red text-white disabled:opacity-50"
            >
              Speichern
            </button>
            <button type="button" onClick={resetForm} className="text-xs px-2 py-1 text-slate-600">
              Abbrechen
            </button>
          </div>
        </div>
      )}
      <table className="w-full text-[11px]">
        <thead className="bg-slate-100 text-slate-600 uppercase text-[10px]">
          <tr>
            <th className="text-left py-1.5 px-2">Schüler</th>
            <th className="text-left py-1.5 px-2">Fach</th>
            <th className="text-left py-1.5 px-2">Wunsch</th>
            <th className="text-left py-1.5 px-2">seit</th>
            <th className="text-left py-1.5 px-2">Bemerkung</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr key={row.id} className="border-b border-slate-100" data-testid={`waitlist-row-${row.id}`}>
              <td className="py-1 px-2 font-medium">{row.student_name || '—'}</td>
              <td className="py-1 px-2">{row.subject_name || '—'}</td>
              <td className="py-1 px-2">
                {row.preferred_day != null ? WEEKDAYS[row.preferred_day] : '—'}
                {row.preferred_time ? ` ${formatTime(row.preferred_time)}` : ''}
              </td>
              <td className="py-1 px-2">{row.requested_on || '—'}</td>
              <td className="py-1 px-2 text-slate-600">{row.note || '—'}</td>
              <td className="py-1 px-2 text-right">
                <button
                  type="button"
                  data-testid={`waitlist-remove-${row.id}`}
                  onClick={() => remove(row.id)}
                  className="text-rose-600 hover:text-rose-700"
                >
                  Entfernen
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {items.length === 0 && (
        <p className="py-8 text-center text-slate-400 text-xs italic" data-testid="waitlist-empty">
          Niemand auf der Warteliste.
        </p>
      )}
    </div>
  );
}
